import { COLORS, toCss } from '../theme';

/**
 * Tints the page-level background that shows through Phaser's letterbox area
 * (the slivers of viewport left over after FIT-mode scales the 720×1280
 * canvas to the device's actual aspect ratio).
 *
 * The canvas itself is always painted by Phaser, so this color is only ever
 * visible at the edges. Matching it to the active scene's primary fill makes
 * the letterbox read as an intentional frame rather than a black band —
 * cream while you're on Home/StageSelect/Closet/Result, ink during gameplay.
 *
 * Mirrors onto both `#app` (Phaser's parent) and `body`: on iOS Chrome the
 * `fitAppToVisualViewport` hook in main.ts shrinks `#app` to the visual
 * viewport's width, so the body shows through whenever the layout viewport
 * is wider than the visible region and we need both surfaces to agree.
 */
let baseColor: number = COLORS.ink;
let overlayActive = false;

function applyColor(n: number) {
  const css = toCss(n);
  document.body.style.backgroundColor = css;
  const app = document.getElementById('app');
  if (app) app.style.backgroundColor = css;
}

export function setLetterboxColor(n: number): void {
  baseColor = n;
  if (!overlayActive) applyColor(n);
}

/**
 * Switch the letterbox + ad-reserve strip to an overlay color while a
 * fullscreen modal is open, so the dim inside the canvas visually extends
 * past the canvas edges. Restoring goes back to whichever color the active
 * scene registered via setLetterboxColor.
 *
 * The canvas-internal dim is a semi-transparent black rectangle laid over
 * the scene's base color, so the *effective* dim tone differs per scene
 * (warm gray over cream, near-black over ink). Callers can pass that
 * effective tone via `color` so the letterbox blends seamlessly with the
 * in-canvas dim; if omitted we fall back to ink, which is close enough for
 * already-dark scenes.
 */
export function setLetterboxOverlay(active: boolean, color?: number): void {
  overlayActive = active;
  applyColor(active ? (color ?? COLORS.ink) : baseColor);
}
